import { floorToTwoDecimalPlaces, roundToTwoDecimalPlaces } from '@/lib/number';
import { Cart } from '@/schema/cart';
import { ICountry } from '@/schema/country';

export type ShippingCompany = 'ems' | 'sagawa' | 'yunda' | 'dhl' | 'indonesia';

// ✅ 카테고리(다국어)의 영어 값이 'Donation'이면 기부 상품으로 판별
export const isDonationShop = (
  category?: { en?: string | null } | null,
): boolean => category?.en === 'Donation';

// ✅ 배송비 산정용 무게: 기부(Donation) 상품 무게는 제외하고 합산
export const getShippingWeight = (carts: Cart[]): number =>
  Number(
    carts
      .filter(cart => !isDonationShop(cart.shop?.category))
      .reduce((sum, cart) => sum + cart.cart.totalWeight, 0)
      .toFixed(2),
  );

// ✅ 배송이 필요한(기부가 아닌) 상품이 하나라도 있는지 여부. 없으면 배송비 0원
export const hasShippableItem = (carts: Cart[]): boolean =>
  carts.some(cart => !isDonationShop(cart.shop?.category));

export const calculatingShippingFee = (
  country: ICountry,
  totalPrice: number,
  totalWeight: number,
  exchangeRate: number,
  currency: string,
  shippingCompany: string = 'ems',
) => {
  totalWeight = parseFloat(totalWeight.toFixed(2));

  let shippingDiscountAmount = 0;
  if (country.freeShippingThreshold > 0) {
    const freeShippingThreshold = roundToTwoDecimalPlaces(
      country.freeShippingThreshold * exchangeRate,
    );
    if (totalPrice >= freeShippingThreshold) {
      shippingDiscountAmount = country.thresholdDiscount;
    }
  }

  const shippingCompanyFee = country.shippingFee[shippingCompany];

  if (shippingCompanyFee) {
    const lastFee = shippingCompanyFee.at(shippingCompanyFee.length - 1);
    const lastWeight = lastFee!.kg;
    const lastFeeCount = parseInt(String(totalWeight / lastWeight));
    const remainWeight = totalWeight % lastWeight;

    const remainFee = shippingCompanyFee.find(
      (data: { kg: number }) => data.kg >= remainWeight,
    );

    const shippingFee =
      currency === 'JPY'
        ? Math.ceil(
            (lastFeeCount * lastFee!.price + remainFee!.price) * exchangeRate,
          )
        : roundToTwoDecimalPlaces(
            (lastFeeCount * lastFee!.price + remainFee!.price) * exchangeRate,
          );
    shippingDiscountAmount = floorToTwoDecimalPlaces(
      shippingDiscountAmount * exchangeRate,
    );

    return Number((shippingFee - shippingDiscountAmount).toFixed(2));
  }

  return 0;
};

export const calculateIndonesiaShippingFeeForRp = (
  country: ICountry,
  totalWeight: number,
) => {
  const indonesiaShippingFee = country.shippingFee.indonesia;
  if (indonesiaShippingFee) {
    const lastFee = indonesiaShippingFee.at(indonesiaShippingFee.length - 1);
    const lastWeight = lastFee!.kg;

    const lastFeeCount = parseInt(String(totalWeight / lastWeight));
    const remainWeight = totalWeight % lastWeight;

    const remainFee = indonesiaShippingFee.find(
      (data: { kg: number }) => data.kg >= remainWeight,
    );

    // 실제 환율이 아닌 달러에 맞춘 환율
    return Math.ceil(
      (lastFeeCount * lastFee!.price + remainFee!.price) * 12.28,
    );
  }

  return 0;
};
